import {instanceToPlain} from 'class-transformer';
import {IsEnum} from 'class-validator';

import {Prettify} from '@ngrx/signals';
import {uuidv4} from '@shared-types';

import {GtmAction, TriggerActionEntity} from '../action';
import {TriggerEntity, TriggerEntityClassMetadata, TriggerEventType} from '../index';
import {AbstractTriggerEntity} from '../trigger.entity';

const ClientEventSharedMetaData: Pick<TriggerEntityClassMetadata, 'type' | 'availableActionClasses'> = {
	type: TriggerEventType.ClientEvent,
	availableActionClasses: [GtmAction],
};

export type ClientEventValueType = enumType<typeof ClientEventTriggerEntity.ClientEventValueType>;

export class ClientEventTriggerEntity extends AbstractTriggerEntity {
	static override _uniqueId: string = uuidv4();

	/* -------------------  Value ------------------- */
	public static readonly ClientEventValueType = {
		OnSignStart: 0,
		OnSignSubmit: 1,
		OnSignReject: 2,
	} as const;

	@IsEnum(ClientEventTriggerEntity.ClientEventValueType, {message: 'trigger.errors.invalidClientEventValue'})
	public value: enumType<typeof ClientEventTriggerEntity.ClientEventValueType> = null;

	/* ------------------- Action ------------------- */
	action: GtmAction;

	protected constructor(init?: Partial<ClientEventTriggerEntity>) {
		super(TriggerEventType.ClientEvent, init);
		if (init?.action) this.action = TriggerActionEntity.create<GtmAction>(init?.action?.type, init?.action);
	}
	public static is: (trigger: any) => trigger is ClientEventTriggerEntity = (trigger): trigger is ClientEventTriggerEntity => trigger.event === TriggerEventType.ClientEvent;
	public static create<T extends ClientEventTriggerEntity>(init?: Partial<T>): T {
		if (!ClientEventTriggerEntity.is(init)) throw new Error('Invalid event type');
		switch (init.value) {
			case ClientEventTriggerEntity.ClientEventValueType.OnSignStart:
				return new SignStartClientEventTrigger(init) as T;
			case ClientEventTriggerEntity.ClientEventValueType.OnSignSubmit:
				return new SignSubmitClientEventTrigger(init) as T;
			case ClientEventTriggerEntity.ClientEventValueType.OnSignReject:
				return new SignRejectClientEventTrigger(init) as T;
			default:
				throw new Error('Invalid value');
		}
	}

	toJSON(): Prettify<TriggerEntity> {
		return instanceToPlain(this, {}) as ClientEventTriggerEntity;
	}
}

// Sign Start Client Event Trigger
@GroupDecorator('trigger-entity', true)
@ClassMetaData<TriggerEntityClassMetadata>({
	...ClientEventSharedMetaData,
	name: 'sign-start-client-event-trigger',
	label: `pages.cw.eventactions.eventtypes.clientevent.options.${ClientEventTriggerEntity.ClientEventValueType.OnSignStart}`,
	summary: `pages.cw.eventactions.eventtypes.clientevent.options.${ClientEventTriggerEntity.ClientEventValueType.OnSignStart}`,
})
export class SignStartClientEventTrigger extends ClientEventTriggerEntity {
	static override _uniqueId: string = uuidv4();
	public override readonly value: typeof ClientEventTriggerEntity.ClientEventValueType.OnSignStart = ClientEventTriggerEntity.ClientEventValueType.OnSignStart;

	public override readonly action: GtmAction;

	constructor(init?: Partial<ClientEventTriggerEntity>) {
		super(init);
		if (init?.action) this.action = TriggerActionEntity.create<GtmAction>(init?.action?.type, init?.action);
		else this.action = new GtmAction();
	}

	override toJSON(): Prettify<TriggerEntity> {
		return instanceToPlain(this, {}) as SignStartClientEventTrigger;
	}
}

// Sign Submit Client Event Trigger
@GroupDecorator('trigger-entity', true)
@ClassMetaData<TriggerEntityClassMetadata>({
	...ClientEventSharedMetaData,
	name: 'sign-submit-client-event-trigger',
	label: `pages.cw.eventactions.eventtypes.clientevent.options.${ClientEventTriggerEntity.ClientEventValueType.OnSignSubmit}`,
	summary: `pages.cw.eventactions.eventtypes.clientevent.options.${ClientEventTriggerEntity.ClientEventValueType.OnSignSubmit}`,
})
export class SignSubmitClientEventTrigger extends ClientEventTriggerEntity {
	static override _uniqueId: string = uuidv4();
	public override readonly value: typeof ClientEventTriggerEntity.ClientEventValueType.OnSignSubmit = ClientEventTriggerEntity.ClientEventValueType.OnSignSubmit;

	public override readonly action: GtmAction;

	constructor(init?: Partial<ClientEventTriggerEntity>) {
		super(init);
		if (init?.action) this.action = TriggerActionEntity.create<GtmAction>(init?.action?.type, init?.action);
		else this.action = new GtmAction();
	}

	override toJSON(): Prettify<TriggerEntity> {
		return instanceToPlain(this, {}) as SignSubmitClientEventTrigger;
	}
}

// Sign Reject Client Event Trigger
@GroupDecorator('trigger-entity', true)
@ClassMetaData<TriggerEntityClassMetadata>({
	...ClientEventSharedMetaData,
	name: 'sign-reject-client-event-trigger',
	label: `pages.cw.eventactions.eventtypes.clientevent.options.${ClientEventTriggerEntity.ClientEventValueType.OnSignReject}`,
	summary: `pages.cw.eventactions.eventtypes.clientevent.options.${ClientEventTriggerEntity.ClientEventValueType.OnSignReject}`,
})
export class SignRejectClientEventTrigger extends ClientEventTriggerEntity {
	static override _uniqueId: string = uuidv4();
	public override readonly value: typeof ClientEventTriggerEntity.ClientEventValueType.OnSignReject = ClientEventTriggerEntity.ClientEventValueType.OnSignReject;

	public override readonly action: GtmAction;

	constructor(init?: Partial<ClientEventTriggerEntity>) {
		super(init);
		if (init?.action) this.action = TriggerActionEntity.create<GtmAction>(init?.action?.type, init?.action);
		else this.action = new GtmAction();
	}

	override toJSON(): Prettify<TriggerEntity> {
		return instanceToPlain(this, {}) as SignRejectClientEventTrigger;
	}
}
